import { cn } from "../../lib/cn";

/**
 * Compact status pill for tool calls and results. Semantic color only (per the
 * design rules) — the word carries the meaning, the tint reinforces it. A
 * "running" badge carries a live pulsing dot. Give the element `key={status}`
 * at the call site so a state change (running → success/error) remounts and
 * replays the quiet `activity-status-pop`.
 */
export type ToolStatus =
  | "running"
  | "success"
  | "error"
  | "unchanged"
  | "skipped"
  | "warning";

const PALETTE: Record<ToolStatus, { fg: string; bg: string; label: string }> = {
  running: { fg: "var(--accent)", bg: "rgb(var(--accent-rgb) / 0.12)", label: "running" },
  success: { fg: "var(--status-success)", bg: "var(--status-success-soft)", label: "done" },
  error: { fg: "var(--status-error)", bg: "var(--status-error-soft)", label: "error" },
  unchanged: { fg: "var(--text-tertiary)", bg: "var(--bg-inset)", label: "unchanged" },
  skipped: { fg: "var(--text-tertiary)", bg: "var(--bg-inset)", label: "skipped" },
  warning: { fg: "var(--status-warning)", bg: "var(--status-warning-soft)", label: "warning" },
};

interface Props {
  status: ToolStatus;
  /** Override the default word (e.g. "created", "updated"). */
  label?: string;
  className?: string;
}

export function StatusBadge({ status, label, className }: Props) {
  const p = PALETTE[status];
  return (
    <span
      className={cn(
        "activity-status-pop inline-flex items-center gap-1 h-[18px] px-1.5 rounded-sm shrink-0",
        "text-mono text-[11px] leading-none",
        className,
      )}
      style={{ color: p.fg, background: p.bg }}
    >
      {status === "running" && (
        <span className="relative inline-flex h-1.5 w-1.5">
          <span
            className="absolute inset-0 rounded-full motion-safe:animate-ping"
            style={{ background: p.fg, opacity: 0.5 }}
          />
          <span
            className="relative inline-block h-1.5 w-1.5 rounded-full"
            style={{ background: p.fg }}
          />
        </span>
      )}
      {label ?? p.label}
    </span>
  );
}
